import instance from '../api';
import { EProjectNames, isTestEnvProject } from '../../Hooks/useProjectData';

// ── Types ──────────────────────────────────────────────────────────────────────

export type TransferStatusValue = 'running' | 'done' | 'partial' | 'failed';
export type TransferMode = 'new' | 'continue';
// company.eldStatus
export type CompanyEldStatus = 'active' | 'migrating' | 'migrated_out';

export interface TransferError {
  // Source-service driver id; absent for company / finish stage errors
  driverId?: number;
  stage: 'logs' | 'trackings' | 'company' | 'finish';
  date?: string;
  message: string;
}

export interface CompanyTransfer {
  migrationId: number;
  // out: left this ELD, in: arrived on this ELD
  direction: 'in' | 'out';
  sourceEld: string;
  targetEld: string;
  targetLegal?: boolean;
  includeTracking?: boolean;
  mode?: TransferMode;
  status: TransferStatusValue;
  remoteCompanyUid?: string | null;
  driversTotal?: number;
  driversDone?: number;
  driversFailed?: number;
  errors?: TransferError[];
  description?: string | null;
  startedAt?: string;
  finishedAt?: string | null;
  company?: {
    isActive: boolean;
    eldStatus: CompanyEldStatus;
    eldStatusDescription: string | null;
  };
  driversRetried?: number[];
}

export interface StartTransferBody {
  targetEld: string;
  targetLegal: boolean;
  includeTracking: boolean;
  mode?: TransferMode;
}

// The record the target already has for this USDOT (409 responses)
export interface ExistingCompany {
  uid: string;
  name: string;
  usdot: string;
  isActive: boolean;
  eldStatus: CompanyEldStatus;
  eldStatusDescription: string | null;
}

// ── Brands ─────────────────────────────────────────────────────────────────────

export const ELD_BRAND_LABELS: Record<string, string> = {
  [EProjectNames.TTELD]: 'TT ELD',
  [EProjectNames.EVO]: 'EVO ELD',
  [EProjectNames.ONTIME]: 'OnTime Logs',
  [EProjectNames.ZIPPY]: 'Zippy ELD',
  [EProjectNames.SBAELD]: 'SBA ELD',
  [EProjectNames.RTE]: 'RTE Logs',
  [EProjectNames.RULER]: 'Ruler ELD',
  [EProjectNames.FLEETVISION]: 'FleetVision',
  [EProjectNames.RRELD]: 'RR ELD',
  [EProjectNames.PROTRUX]: 'Protrux',
  [EProjectNames.FIVEELD]: 'Five ELD',
  [EProjectNames.DELTAELD]: 'Delta ELD',
  [EProjectNames.UATELD]: 'UAT ELD',
  [EProjectNames.BTEST]: 'BTEST ELD',
};

export const eldBrandLabel = (eld?: string | null) =>
  eld ? ELD_BRAND_LABELS[eld] || eld.toUpperCase() : '-';

// Every known brand except the current one; the test ELDs are offered only from a test ELD
export const transferTargets = (currentProject?: string) =>
  Object.keys(ELD_BRAND_LABELS).filter(
    (eld) =>
      eld !== currentProject &&
      (isTestEnvProject(currentProject) || !isTestEnvProject(eld)),
  );

// ── Errors ─────────────────────────────────────────────────────────────────────

// Backend error shape: { error: { statusCode, message, data } }
export const transferErrorMessage = (error: any, fallback = 'Something went wrong') => {
  const msg: string = error?.response?.data?.error?.message || error?.message || fallback;
  if (/targetEld is invalid/i.test(msg)) return 'Target ELD is invalid';
  return msg;
};

export const transferErrorData = (error: any) => error?.response?.data?.error?.data;
export const transferErrorStatus = (error: any): number | undefined =>
  error?.response?.data?.error?.statusCode ?? error?.response?.status;

// ── API ────────────────────────────────────────────────────────────────────────

const base = (companyId: string | number) => `admins/migrate-company/${companyId}`;

export const companyTransfer = {
  async start(companyId: string | number, body: StartTransferBody): Promise<CompanyTransfer> {
    const { data } = await instance.post(base(companyId), body);
    return data;
  },
  // Latest transfer (in or out); null when the company was never transferred
  async status(companyId: string | number): Promise<CompanyTransfer | null> {
    try {
      const { data } = await instance.get(`${base(companyId)}/status`);
      return data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },
  async retry(companyId: string | number): Promise<CompanyTransfer> {
    const { data } = await instance.post(`${base(companyId)}/retry`);
    return data;
  },
  async history(companyId: string | number): Promise<CompanyTransfer[]> {
    const { data } = await instance.get(`${base(companyId)}/history`);
    return data ?? [];
  },
};
